// 서식(프리셋) — 지금 그린 객체와 **필드 팔레트로 놓은 필드**를 이름 붙여 저장하고, 저장된
// 서식을 다른 양식에서 불러온다(오너 요청 2026-09-22 · 필드는 09-23). 서명자 역할·PDF 는
// 양식 것이라 오가지 않는다.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LayoutObject, SignatureField } from '@bhops/shared';
import { signatureLayoutPresetsApi } from '@/api/signatureLayoutPresets';
import { extractApiError } from '@/api/client';
import { useToast } from '@/components/Toast';
import { kstDateTime } from '@bhops/shared';
import { Icon } from '@/components/Icon';

const PRESETS_KEY = ['signature-layout-presets'] as const;

interface Props {
  /** 지금 캔버스의 객체 — '서식으로 저장' 이 이걸 올린다. */
  objects: LayoutObject[];
  /** 지금 필드 팔레트로 놓은 필드(표 칸이 만든 필드는 빼고). */
  fields: SignatureField[];
  /** 불러오기 — 페이지가 캔버스에 붙인다(id 는 새로 준다). */
  onLoad: (objects: LayoutObject[], fields: SignatureField[]) => void;
  onClose: () => void;
}

export function LayoutPresetDialog({ objects, fields, onLoad, onClose }: Props) {
  const empty = objects.length === 0 && fields.length === 0;
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const { data: presets = [], isLoading } = useQuery({
    queryKey: PRESETS_KEY,
    queryFn: () => signatureLayoutPresetsApi.list(),
  });

  const save = useMutation({
    mutationFn: () => signatureLayoutPresetsApi.create(name.trim(), objects, fields),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PRESETS_KEY });
      toast.success(`'${name.trim()}' 서식을 저장했습니다.`);
      setName('');
    },
    onError: (e) => toast.error(extractApiError(e), '서식 저장 실패'),
  });

  const load = useMutation({
    mutationFn: (id: string) => signatureLayoutPresetsApi.get(id),
    onSuccess: (detail) => {
      onLoad(detail.objects, detail.fields ?? []);
      toast.success(
        `'${detail.name}' 서식을 붙였습니다. 객체 ${detail.objects.length}개, 필드 ${(detail.fields ?? []).length}개입니다.`,
      );
      onClose();
    },
    onError: (e) => toast.error(extractApiError(e), '서식 불러오기 실패'),
  });

  const archive = useMutation({
    mutationFn: (id: string) => signatureLayoutPresetsApi.archive(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PRESETS_KEY });
      setConfirmId(null);
    },
    onError: (e) => toast.error(extractApiError(e), '서식 지우기 실패'),
  });

  return (
    <div
      role="dialog"
      aria-label="서식"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'var(--bg-overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: 520,
          maxHeight: '80vh',
          overflow: 'auto',
          background: 'var(--bg-surface)',
          borderRadius: 'var(--r-card)',
          boxShadow: 'var(--shadow-lg)',
          padding: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700, flex: 1 }}>서식</div>
          <button type="button" onClick={onClose} aria-label="닫기" style={btn}>
            <Icon name="x" size={14} />
          </button>
        </div>

        <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={title}>
            지금 그린 객체 {objects.length}개와 필드 {fields.length}개를 서식으로 저장
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="서식 이름 (예: 인수증 기본)"
              aria-label="서식 이름"
              maxLength={80}
              style={{ ...btn, flex: 1, cursor: 'text' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim() && !empty) save.mutate();
              }}
            />
            <button
              type="button"
              data-testid="preset-save"
              disabled={!name.trim() || empty || save.isPending}
              onClick={() => save.mutate()}
              style={{
                ...btn,
                background: 'var(--fg-brand)',
                color: 'var(--bg-surface)',
                border: 'none',
                fontWeight: 600,
              }}
            >
              {save.isPending ? '저장 중…' : '저장'}
            </button>
          </div>
          {empty && (
            <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
              캔버스에 객체도 필드도 없어 저장할 것이 없습니다.
            </span>
          )}
        </section>

        <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={title}>저장된 서식 불러오기</div>
          {isLoading && (
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>불러오는 중…</span>
          )}
          {!isLoading && presets.length === 0 && (
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              저장된 서식이 없습니다.
            </span>
          )}
          {presets.map((p) => (
            <div
              key={p.id}
              data-testid="preset-item"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--r-card)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {p.name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  객체 {p.objectCount}개 · 필드 {p.fieldCount ?? 0}개 · {p.createdByName ?? '—'} ·{' '}
                  {kstDateTime(p.createdAt) ?? ''}
                </div>
              </div>
              {confirmId === p.id ? (
                <>
                  <button type="button" style={btn} onClick={() => setConfirmId(null)}>
                    취소
                  </button>
                  <button
                    type="button"
                    data-testid="preset-delete-confirm"
                    onClick={() => archive.mutate(p.id)}
                    style={{
                      ...btn,
                      background: 'var(--fg-danger)',
                      color: 'var(--bg-surface)',
                      border: 'none',
                    }}
                  >
                    지우기
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    aria-label={`${p.name} 지우기`}
                    style={btn}
                    onClick={() => setConfirmId(p.id)}
                  >
                    지우기
                  </button>
                  <button
                    type="button"
                    data-testid="preset-load"
                    disabled={load.isPending}
                    onClick={() => load.mutate(p.id)}
                    style={{
                      ...btn,
                      background: 'var(--fg-brand)',
                      color: 'var(--bg-surface)',
                      border: 'none',
                      fontWeight: 600,
                    }}
                  >
                    불러오기
                  </button>
                </>
              )}
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}

const title = {
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
} as const;
const btn = {
  padding: '6px 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-btn)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  cursor: 'pointer',
  fontSize: 12.5,
} as const;
